import {
  Dropdown,
  FormLabel,
  MenuItem,
  Typography,
} from '@one-thd/sui-atomic-components';
import { DateTime } from 'luxon';
import React, { ChangeEvent, useState } from 'react';
import { GetProfileDetails } from '../../api/b2b/account';
import { useBusinessToolsContext } from '../../hooks/useContexts';
import { IJobSpend } from '../../models/business-tools';
import { pushEventInfo } from '../../utils/analytics';
import { DateSelectionDropdown } from '../shared/DateSelectionDropdown';

import '../../styles/business-tools.scss';

export interface BusinessHeaderProps {
  businessName?: string;
}

// TODO: Replace Luxon's DateTime.local().toLocaleString(DateTime.DATE_HUGE) with Day.js equivalent
// const formattedDate = dayjs().format('MMMM D, YYYY');

const createJobsMenu = (jobsArray?: IJobSpend[]) => {
  // push default jobName values as they are case sensitive
  const jobsArrayUpperCase: string[] = [];
  // array used to find indexOf default jobName
  const noDuplicateNamesToUpperCase: string[] = [];
  // sort jobName array
  const sortNames: string[] = [];

  // Change to upper case
  if (jobsArray) {
    for (let i = 0; i < jobsArray.length; i += 1) {
      if (jobsArray[i].jobName !== '') {
        jobsArrayUpperCase.push(
          typeof jobsArray[i] !== 'number'
            ? jobsArray[i].jobName.toUpperCase()
            : jobsArray[i].jobName,
        );

        // find duplicates and don't add them to the arrays
        if (
          noDuplicateNamesToUpperCase.indexOf(jobsArrayUpperCase[i]) === -1 &&
          jobsArrayUpperCase[i] !== ''
        ) {
          noDuplicateNamesToUpperCase.push(jobsArrayUpperCase[i]);
          sortNames.push(jobsArrayUpperCase[i]);
        }
      }
    }
  }
  sortNames.sort();

  // map over sortNames array
  return sortNames.map((data, i) => {
    // stores indexOf value
    let index;
    if (typeof data !== 'number') {
      // find indexOf sorted item inside unsorted array
      index = noDuplicateNamesToUpperCase.indexOf(data);
      return (
        <MenuItem value={noDuplicateNamesToUpperCase[index]} key={i}>
          {data}
        </MenuItem>
      );
    }
    // find numerical indexOf sorted item inside unsorted array
    index = noDuplicateNamesToUpperCase.indexOf(data);
    return (
      <MenuItem value={noDuplicateNamesToUpperCase[index]} key={i}>
        {data}
      </MenuItem>
    );
  });
};

const renderPoJobNameElement = (
  selectedPoJobName: string,
  setSelectedPoJobName: (value: string) => void,
  poJobNames: IJobSpend[],
  requestData: {
    startDate: string;
    endDate: string;
    poJobName?: string;
  },
  setRequestData: (value: {
    startDate: string;
    endDate: string;
    poJobName?: string;
  }) => void,
) => {
  return (
    <div>
      <FormLabel htmlFor="jobSelect" id="jobSelectLabel">
        PO/Job Name
      </FormLabel>
      <Dropdown
        id="jobSelect"
        aria-labelledby="jobSelectLabel"
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const eventValue = event.target.value;
          pushEventInfo({
            category: { primaryCategory: 'button submission' },
            eventInfo: { eventName: 'my account settings' },
            stash: {
              myAccount: {
                settings: 'track business spend: filter by job name',
              },
            },
          });
          if (setRequestData) {
            if (eventValue !== 'all') {
              setSelectedPoJobName(eventValue);
              setRequestData({ ...requestData, poJobName: eventValue });
            }
            if (eventValue === 'all') {
              setSelectedPoJobName('all');
              setRequestData({ ...requestData, poJobName: undefined });
            }
          }
        }} // this.handleJobSelectorChange
        value={selectedPoJobName}
        style={{ width: '100%', borderTop: '1px solid #999' }}
      >
        <MenuItem value="all">All PO/Job Names</MenuItem>
        {createJobsMenu(poJobNames)}
      </Dropdown>
    </div>
  );
};

const getCombinedTotalSpend = (topJobs?: IJobSpend[]) => {
  const result = topJobs?.reduce((accumulator, job: IJobSpend) => {
    return accumulator + job.totalSpend;
  }, 0);

  return result?.toLocaleString('en', {
    minimumFractionDigits: 2,
  });
};

export const BusinessToolsHeader = () => {
  const {
    jobSpends,
    dateSelectorValue,
    setDateSelectorValue,
    requestData,
    setRequestData,
  } = useBusinessToolsContext();
  const { profileData } = GetProfileDetails();

  const [selectedPoJobName, setSelectedPoJobName] = useState('all');

  return (
    <div className="sui-grid sui-grid-cols-1  business-tools__topHeader">
      <Typography variant="h2" weight="display">
        {profileData?.accountProfile?.customerProfile?.businessName}
      </Typography>

      <div className="sui-flex sui-justify-between">
        <div>
          <div className="business-tools__topHeader__dateHeader">
            Total Spend as of today
          </div>
          <div className="business-tools__topHeader__dateFull">
            {DateTime.local().toLocaleString(DateTime.DATE_HUGE)}
          </div>
        </div>

        <div className="business-tools__topHeader__headerTotalSpend">
          <span className="business-tools__topHeader__headerDollarSign">$</span>
          {getCombinedTotalSpend(jobSpends)}
        </div>
      </div>

      <div className="sui-flex sui-justify-between" style={{ paddingTop: 10 }}>
        <div className="sui-grid sm:sui-grid-cols-1 md:sui-grid-cols-1 lg:sui-grid-cols-2 sui-gap-2">
          <DateSelectionDropdown
            label="View History From"
            dateSelectorValue={dateSelectorValue}
            setDateSelectorValue={setDateSelectorValue}
            page="business-tools"
          />
          {renderPoJobNameElement(
            selectedPoJobName,
            setSelectedPoJobName,
            jobSpends,
            requestData,
            setRequestData,
          )}
        </div>

        <div />
      </div>
    </div>
  );
};
